import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabase/supabase';

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const AdminDirectChargeFinancePanel = () => {
  const [report, setReport] = useState({ summary: {}, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');

    const { data, error: requestError } = await supabase.rpc(
      'admin_get_direct_charge_finance_1793072000000'
    );

    if (requestError) setError(requestError.message);
    else setReport(data || { summary: {}, transactions: [] });

    setLoading(false);
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  if (loading) {
    return <div className="rounded-2xl bg-white p-6 text-sm font-bold text-indigo-600">Loading charge reconciliation...</div>;
  }

  return (
    <section className="space-y-5">
      {error && <p className="rounded-xl bg-red-50 p-4 text-sm font-bold text-red-700">{error}</p>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[
          ['Direct charges', report.summary.direct_charge_count],
          ['Destination charges', report.summary.destination_charge_count],
          ['Platform commission', money(report.summary.platform_commission)],
          ['Platform tax application fee', money(report.summary.platform_tax_application_fee)],
          ['Combined application fees', money(report.summary.combined_application_fees)],
          ['Stripe tax collected', money(report.summary.stripe_tax_collected)],
          ['Tax retained by platform', money(report.summary.tax_retained_by_platform)],
          ['Platform-retained online fees', money(report.summary.online_order_fees)],
          ['Online-fee refunds', money(report.summary.online_order_fee_refunds)],
          ['Application-fee refunds', money(report.summary.application_fee_refunds)],
          ['Net platform fees', money(report.summary.net_platform_fees)],
          ['Disputed orders', report.summary.disputed_orders]
        ].map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-slate-100 bg-white p-5">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
            <p className="mt-2 text-xl font-black text-slate-900">{value ?? '—'}</p>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white">
        <table className="w-full min-w-[1750px] text-left text-xs">
          <thead className="bg-slate-50 text-[9px] uppercase tracking-widest text-slate-500">
            <tr>
              {[
                'Order',
                'Charge type',
                'Charge account',
                'Tax liable party',
                'Stripe tax',
                'Tax retained by platform',
                'Platform commission',
                'Tax portion of application fee',
                'Combined application fee',
                'Online fee',
                'Fee refunded',
                'Application fee refunded',
                'Refunded total',
                'Dispute'
              ].map((label) => (
                <th key={label} className="px-4 py-3">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(report.transactions || []).map((row) => (
              <tr key={row.order_id}>
                <td className="px-4 py-3 font-black text-indigo-600">{row.order_reference}</td>
                <td className="px-4 py-3">{row.charge_type || 'Not recorded'}</td>
                <td className="px-4 py-3 font-mono">{row.charge_account_id || 'Not recorded'}</td>
                <td className="px-4 py-3">{row.stripe_tax_liable_party || 'Not recorded'}</td>
                <td className="px-4 py-3">
                  {row.stripe_tax_amount_cents == null
                    ? 'Pending'
                    : money(Number(row.stripe_tax_amount_cents) / 100)}
                </td>
                <td className="px-4 py-3">
                  {row.tax_retained_by_platform_cents == null
                    ? 'Not recorded'
                    : money(Number(row.tax_retained_by_platform_cents) / 100)}
                </td>
                <td className="px-4 py-3">
                  {row.platform_commission_cents == null
                    ? 'Not recorded'
                    : money(Number(row.platform_commission_cents) / 100)}
                </td>
                <td className="px-4 py-3">
                  {row.platform_tax_application_fee_cents == null
                    ? 'Not recorded'
                    : money(Number(row.platform_tax_application_fee_cents) / 100)}
                </td>
                <td className="px-4 py-3">{money(row.combined_application_fee_amount)}</td>
                <td className="px-4 py-3">{money(Number(row.online_order_fee_cents || 0) / 100)}</td>
                <td className="px-4 py-3">{money(Number(row.online_order_fee_refunded_cents || 0) / 100)}</td>
                <td className="px-4 py-3">{money(Number(row.application_fee_refunded_cents || 0) / 100)}</td>
                <td className="px-4 py-3">{money(Number(row.refund_amount_cents || 0) / 100)}</td>
                <td className="px-4 py-3">{row.dispute_status || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default AdminDirectChargeFinancePanel;
